import React, { useEffect, useRef, useCallback } from 'react';

export default function EditorFloatingToolbar({
  visible,
  selectedFieldIndex,
  pdfFields,
  configuration,
  overlayRef,
  onSaveField,
  onDuplicateField,
  onDeleteField
}) {
  const toolbarRef = useRef(null);

  // Get current field config
  const field = selectedFieldIndex !== null ? pdfFields[selectedFieldIndex] : null;
  const fieldConfig = field
    ? configuration?.fields?.find(f => field.configId && f.id === field.configId)
      || [...(configuration?.fields || [])].reverse().find(f => f.pdfFieldName === field.name && f.isVisible !== false)
    : null;
  const displayType = fieldConfig?.displayType || 'text';
  const isCheckboxOrRadio = ['checkbox', 'radio'].includes(displayType);

  // Show toolbar for all field types (including checkbox/radio)
  const shouldShow = visible && field;

  // State for toolbar values
  const [fontFamily, setFontFamily] = React.useState('Times New Roman');
  const [fontSize, setFontSize] = React.useState('');
  const [bold, setBold] = React.useState(false);
  const [italic, setItalic] = React.useState(false);
  const [underline, setUnderline] = React.useState(false);
  const [fontColor, setFontColor] = React.useState('#000000');
  const [required, setRequired] = React.useState(false);
  const [maxLength, setMaxLength] = React.useState('');
  const [textAlign, setTextAlign] = React.useState('left');

  // Sync from config when field or field config changes
  useEffect(() => {
    if (!field) return;

    if (fieldConfig) {
      setFontFamily(fieldConfig.fontFamily || 'Times New Roman');
      setFontSize(fieldConfig.fontSize != null ? String(fieldConfig.fontSize) : '');
      setBold(!!fieldConfig.bold);
      setItalic(!!fieldConfig.italic);
      setUnderline(!!fieldConfig.underline);
      setFontColor(fieldConfig.fontColor || '#000000');
      setRequired(!!fieldConfig.required);
      setMaxLength(fieldConfig.maxLength != null ? String(fieldConfig.maxLength) : '');
      setTextAlign(fieldConfig.textAlign || 'left');
    } else {
      setFontFamily('Helvetica');
      setFontSize('');
      setBold(false);
      setItalic(false);
      setUnderline(false);
      setFontColor('#000000');
      setRequired(false);
      setMaxLength('');
      setTextAlign('left');
    }
  }, [field, fieldConfig]);

  const positionToolbar = useCallback(() => {
    if (!toolbarRef.current || !shouldShow || selectedFieldIndex === null) return;

    const highlight = document.querySelector(`[data-field-index="${selectedFieldIndex}"]`);
    if (!highlight) return;

    const highlightRect = highlight.getBoundingClientRect();
    const toolbarHeight = toolbarRef.current.offsetHeight || 34;
    const toolbarWidth = toolbarRef.current.offsetWidth || 400;

    let top = highlightRect.top - toolbarHeight - 8;
    let left = highlightRect.left + (highlightRect.width / 2) - (toolbarWidth / 2);

    left = Math.max(8, Math.min(left, window.innerWidth - toolbarWidth - 8));

    const shouldPlaceBelow = top < 56;
    if (shouldPlaceBelow) {
      top = highlightRect.bottom + 8;
    }

    top = Math.max(56, Math.min(top, window.innerHeight - toolbarHeight - 8));

    toolbarRef.current.style.top = `${top}px`;
    toolbarRef.current.style.left = `${left}px`;
    toolbarRef.current.classList.toggle('toolbar-below', shouldPlaceBelow);
  }, [overlayRef, selectedFieldIndex, shouldShow]);

  useEffect(() => {
    positionToolbar();
  }, [positionToolbar, shouldShow, fontFamily, fontSize, bold, italic, underline, fontColor, required, maxLength, textAlign]);

  useEffect(() => {
    const wrapper = overlayRef.current?.closest('.pdf-canvas-wrapper');
    const container = overlayRef.current?.closest('.editor-center-panel');
    if (!wrapper) return;

    const handlePosition = () => positionToolbar();
    wrapper.addEventListener('scroll', handlePosition, true);
    container?.addEventListener('scroll', handlePosition, true);
    document.addEventListener('scroll', handlePosition, true);
    window.addEventListener('resize', handlePosition);

    return () => {
      wrapper.removeEventListener('scroll', handlePosition, true);
      container?.removeEventListener('scroll', handlePosition, true);
      document.removeEventListener('scroll', handlePosition, true);
      window.removeEventListener('resize', handlePosition);
    };
  }, [overlayRef, positionToolbar]);

  // Update the canonical editor state immediately. Network coalescing belongs to the
  // shared queue, not a delayed callback holding stale bounds or a previous selection.
  const autoSave = useCallback((changes) => {
    if (onSaveField && fieldConfig) onSaveField(fieldConfig.id, changes);
  }, [onSaveField, fieldConfig]);

  if (!shouldShow) return null;

  return (
    <div
      ref={toolbarRef}
      className="field-toolbar"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Font Family - hidden for checkbox/radio */}
      {!isCheckboxOrRadio && (
        <select
          className="toolbar-select"
          value={fontFamily}
          onChange={(e) => { setFontFamily(e.target.value); autoSave({ fontFamily: e.target.value || null }); }}
        >
          <option value="Times New Roman">Times New Roman</option>
          <option value="Arial">Arial</option>
          <option value="Courier New">Courier New</option>
          <option value="Georgia">Georgia</option>
          <option value="Verdana">Verdana</option>
          <option value="Helvetica">Helvetica</option>
          <option value="Tahoma">Tahoma</option>
          <option value="Trebuchet MS">Trebuchet MS</option>
          <option value="Comic Sans MS">Comic Sans MS</option>
          <option value="Raleway">Raleway</option>
        </select>
      )}

      {!isCheckboxOrRadio && <div className="toolbar-divider" />}

      {/* Font Size - hidden for checkbox/radio */}
      {!isCheckboxOrRadio && (
        <select
          className="toolbar-select"
          value={fontSize}
          onChange={(e) => { setFontSize(e.target.value); autoSave({ fontSize: e.target.value === '' ? null : parseInt(e.target.value) }); }}
          style={{ width: '70px' }}
        >
          <option value="">Auto</option>
          <option value="8">8</option>
          <option value="9">9</option>
          <option value="10">10</option>
          <option value="11">11</option>
          <option value="12">12</option>
          <option value="14">14</option>
          <option value="16">16</option>
          <option value="18">18</option>
          <option value="20">20</option>
          <option value="24">24</option>
          <option value="28">28</option>
          <option value="32">32</option>
          <option value="36">36</option>
          <option value="48">48</option>
          <option value="72">72</option>
        </select>
      )}

      {!isCheckboxOrRadio && <div className="toolbar-divider" />}

      {/* Bold - hidden for checkbox/radio */}
      {!isCheckboxOrRadio && <button
        className={`toolbar-btn${bold ? ' active' : ''}`}
        title="Bold"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const newValue = !bold;
          setBold(newValue);
          autoSave({ bold: newValue });
        }}
      >
        <strong>B</strong>
      </button>}

      {/* Italic - hidden for checkbox/radio */}
      {!isCheckboxOrRadio && <button
        className={`toolbar-btn${italic ? ' active' : ''}`}
        title="Italic"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const newValue = !italic;
          setItalic(newValue);
          autoSave({ italic: newValue });
        }}
      >
        <em>I</em>
      </button>}

      {/* Underline - hidden for checkbox/radio */}
      {!isCheckboxOrRadio && <button
        className={`toolbar-btn${underline ? ' active' : ''}`}
        title="Underline"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const newValue = !underline;
          setUnderline(newValue);
          autoSave({ underline: newValue });
        }}
      >
        <u>U</u>
      </button>}

      {!isCheckboxOrRadio && <div className="toolbar-divider" />}

      {/* Text Alignment - hidden for checkbox/radio */}
      {!isCheckboxOrRadio && <button
        className={`toolbar-btn${textAlign === 'left' ? ' active' : ''}`}
        title="Align Left"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setTextAlign('left');
          autoSave({ textAlign: null });
        }}
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
          <line x1="1" y1="2.5" x2="13" y2="2.5"/>
          <line x1="1" y1="5.5" x2="9" y2="5.5"/>
          <line x1="1" y1="8.5" x2="13" y2="8.5"/>
          <line x1="1" y1="11.5" x2="9" y2="11.5"/>
        </svg>
      </button>}
      {!isCheckboxOrRadio && <button
        className={`toolbar-btn${textAlign === 'center' ? ' active' : ''}`}
        title="Align Center"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setTextAlign('center');
          autoSave({ textAlign: 'center' });
        }}
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
          <line x1="1" y1="2.5" x2="13" y2="2.5"/>
          <line x1="3" y1="5.5" x2="11" y2="5.5"/>
          <line x1="1" y1="8.5" x2="13" y2="8.5"/>
          <line x1="3" y1="11.5" x2="11" y2="11.5"/>
        </svg>
      </button>}
      {!isCheckboxOrRadio && <button
        className={`toolbar-btn${textAlign === 'right' ? ' active' : ''}`}
        title="Align Right"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setTextAlign('right');
          autoSave({ textAlign: 'right' });
        }}
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
          <line x1="1" y1="2.5" x2="13" y2="2.5"/>
          <line x1="5" y1="5.5" x2="13" y2="5.5"/>
          <line x1="1" y1="8.5" x2="13" y2="8.5"/>
          <line x1="5" y1="11.5" x2="13" y2="11.5"/>
        </svg>
      </button>}

      {!isCheckboxOrRadio && <div className="toolbar-divider" />}

      {/* Font Color - hidden for checkbox/radio */}
      {!isCheckboxOrRadio && (
        <input
          type="color"
          className="toolbar-color"
          value={fontColor}
          onChange={(e) => { setFontColor(e.target.value); autoSave({ fontColor: e.target.value === '#000000' ? null : e.target.value }); }}
        />
      )}

      {!isCheckboxOrRadio && <div className="toolbar-divider" />}

      {/* Required */}
      <label className={`toolbar-btn toolbar-required-btn${required ? ' active' : ''}`} title="Required">
        <input
          type="checkbox"
          checked={required}
          onChange={(e) => { setRequired(e.target.checked); autoSave({ required: e.target.checked }); }}
          style={{ display: 'none' }}
        />
        <span>*</span>
      </label>

      <div className="toolbar-divider" />

      {/* Max Length - hidden for checkbox/radio */}
      {!isCheckboxOrRadio && (
        <input
          type="number"
          className="toolbar-input toolbar-maxlen"
          value={maxLength}
          placeholder="Max"
          min="0"
          onChange={(e) => { setMaxLength(e.target.value); autoSave({ maxLength: e.target.value ? parseInt(e.target.value) : null }); }}
          onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
        />
      )}

      {!isCheckboxOrRadio && <div className="toolbar-divider" />}

      {/* Duplicate Field */}
      <button
        className="toolbar-btn"
        title="Duplicate this field"
        onClick={() => { if (onDuplicateField && fieldConfig) onDuplicateField(fieldConfig); }}
        style={{ fontSize: '1rem' }}
      >
        ⧉
      </button>

      {/* Delete Field */}
      <button
        className="toolbar-btn toolbar-delete-btn"
        title="Delete this field"
        onClick={() => { if (onDeleteField && selectedFieldIndex !== null) onDeleteField(selectedFieldIndex); }}
        style={{ fontSize: '1rem', color: '#d32f2f' }}
      >
        🗑
      </button>
    </div>
  );
}
